import type { DoseResult } from '../types/simulation';

/**
 * REKOVELLE (follitropin delta) daily starting dose — first treatment cycle.
 * Mirrors the labelled SmPC dosing table: AMH (pmol/L, Elecsys® AMH Plus,
 * whole numbers) and body weight (kg).
 *
 *  AMH < 15            → 12 μg, independent of body weight
 *  AMH ≥ 15            → μg/kg by AMH band below
 *  Daily dose limits   → minimum 6 μg, maximum 12 μg
 */
type DoseBand = { min: number; max: number; perKg: number; label: string };

export const LOW_AMH_THRESHOLD = 15;
export const LOW_AMH_FIXED_DOSE = 12;
export const MIN_DAILY_DOSE = 6;
export const MAX_DAILY_DOSE = 12;

export const DOSE_BANDS: DoseBand[] = [
  { min: 15, max: 16, perKg: 0.19, label: '15–16' },
  { min: 17, max: 17, perKg: 0.18, label: '17' },
  { min: 18, max: 18, perKg: 0.17, label: '18' },
  { min: 19, max: 20, perKg: 0.16, label: '19–20' },
  { min: 21, max: 22, perKg: 0.15, label: '21–22' },
  { min: 23, max: 24, perKg: 0.14, label: '23–24' },
  { min: 25, max: 27, perKg: 0.13, label: '25–27' },
  { min: 28, max: 32, perKg: 0.12, label: '28–32' },
  { min: 33, max: 39, perKg: 0.11, label: '33–39' },
  { min: 40, max: Infinity, perKg: 0.1, label: '≥40' },
];

export function rekovelleDose(amhInput: number, weightInput: number): DoseResult {
  const amh = Math.round(amhInput);
  const weight = Math.round(weightInput);

  if (amh < LOW_AMH_THRESHOLD) {
    return { dose: LOW_AMH_FIXED_DOSE, amhBand: '<15', perKg: null, calculated: null, limit: null };
  }

  const band = DOSE_BANDS.find((b) => amh >= b.min && amh <= b.max) ?? DOSE_BANDS[DOSE_BANDS.length - 1];
  const calculated = round2(band.perKg * weight);
  const limit = calculated > MAX_DAILY_DOSE ? 'max' : calculated < MIN_DAILY_DOSE ? 'min' : null;
  const dose = Math.min(MAX_DAILY_DOSE, Math.max(MIN_DAILY_DOSE, calculated));

  return { dose, amhBand: band.label, perKg: band.perKg, calculated, limit };
}

export function describeDoseRule(d: DoseResult, weight: number): string {
  if (d.perKg === null) return 'AMH <15 → 12 μg, independent of weight';
  const base = `AMH ${d.amhBand} → ${d.perKg.toFixed(2)} μg/kg × ${Math.round(weight)} kg`;
  if (d.limit === 'max') return `${base} = ${d.calculated?.toFixed(2)} → max 12 μg`;
  if (d.limit === 'min') return `${base} = ${d.calculated?.toFixed(2)} → min 6 μg`;
  return base;
}

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}
